import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, ChevronDown, BookOpen, Star } from "lucide-react";
import { HERO } from "@/constants/testIds";

const HEADLINE_LINES = ["Where every bedtime", "becomes a magical", "adventure"];

const lineVariants = {
  hidden: { y: 90, opacity: 0, clipPath: "inset(0 0 100% 0)" },
  visible: (i) => ({
    y: 0, opacity: 1, clipPath: "inset(0 0 0% 0)",
    transition: { duration: 0.85, delay: i * 0.22 + 0.3, ease: [0.22, 1, 0.36, 1] }
  }),
};

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: "easeOut" } },
});

export default function HeroSection({ onCTA }) {
  const containerRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  const moonY = useTransform(scrollY, [0, 600], [0, 120]);
  const moonOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  useEffect(() => {
    const handleMouse = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 25,
        y: (e.clientY / window.innerHeight - 0.5) * 25,
      });
    };
    window.addEventListener("mousemove", handleMouse);
    return () => window.removeEventListener("mousemove", handleMouse);
  }, []);

  const scrollToGenerator = () => {
    const el = document.getElementById("generator");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    if (onCTA) onCTA();
  };

  const STATS = [
    { value: "10,000+", label: "Stories Created" },
    { value: "20 min", label: "Avg. Read Time" },
    { value: "10–14", label: "Illustrations Each" },
  ];

  return (
    <section
      data-testid={HERO.section}
      ref={containerRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16"
    >
      {/* Radial gradient overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(109,40,217,0.18) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 80% 70%, rgba(245,158,11,0.08) 0%, transparent 60%)"
      }} />

      {/* 3D Floating Moon */}
      <motion.div
        style={{ y: moonY, opacity: moonOpacity }}
        className="absolute right-2 top-20 opacity-30 sm:opacity-100 sm:right-8 sm:top-24 lg:right-20 lg:top-32 pointer-events-none"
        animate={{ rotate: [0, 3, -3, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div
          style={{
            transform: `perspective(800px) rotateX(${mousePos.y * 0.4}deg) rotateY(${mousePos.x * 0.4}deg)`,
            transition: "transform 0.1s ease-out",
          }}
        >
          <div className="relative w-20 h-20 sm:w-40 sm:h-40 lg:w-64 lg:h-64 animate-float">
            <div
              className="w-full h-full rounded-full"
              style={{
                background: "radial-gradient(circle at 35% 35%, #FCD34D 0%, #F59E0B 45%, #92400E 100%)",
                boxShadow: "0 0 60px rgba(252,211,77,0.35), 0 0 120px rgba(245,158,11,0.15), inset -20px -20px 40px rgba(0,0,0,0.4)"
              }}
            />
            {/* Moon surface details */}
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className="absolute top-[20%] left-[25%] w-[15%] h-[15%] rounded-full bg-amber-900/30" />
              <div className="absolute top-[50%] left-[55%] w-[10%] h-[10%] rounded-full bg-amber-900/25" />
              <div className="absolute top-[65%] left-[30%] w-[8%] h-[8%] rounded-full bg-amber-900/20" />
            </div>
            {/* Glow rings */}
            <div className="absolute inset-[-20%] rounded-full border border-amber-400/10 animate-ping" style={{ animationDuration: "3s" }} />
            <div className="absolute inset-[-40%] rounded-full border border-amber-400/05" />
          </div>
        </motion.div>
      </motion.div>

      {/* Hero content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8">
        {/* Badge */}
        <motion.div
          variants={fadeUp(0.1)}
          initial="hidden"
          animate="visible"
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-mono tracking-[0.15em] uppercase border border-amber-400/30 bg-amber-500/10 text-amber-300">
            <Sparkles size={11} />
            AI-Personalized Bedtime Stories
          </span>
        </motion.div>

        {/* Kinetic headline */}
        <h1 data-testid={HERO.headline} className="mb-8 max-w-3xl">
          {HEADLINE_LINES.map((line, i) => (
            <div key={i} className="clip-text overflow-hidden">
              <motion.div
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block font-serif font-bold leading-[1.08] ${
                  i === 2
                    ? "text-4xl sm:text-5xl lg:text-7xl gradient-text"
                    : "text-4xl sm:text-5xl lg:text-7xl text-white"
                }`}
              >
                {line}
              </motion.div>
            </div>
          ))}
        </h1>

        {/* Subtitle */}
        <motion.p
          variants={fadeUp(0.8)}
          initial="hidden"
          animate="visible"
          className="text-base sm:text-lg text-slate-400 max-w-xl mb-10 leading-relaxed"
        >
          Your child becomes the hero. Every story is a magical 20-minute journey crafted just for them — with their name woven through vivid illustrated worlds.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={fadeUp(1.0)}
          initial="hidden"
          animate="visible"
          className="flex flex-wrap items-center gap-4 mb-16"
        >
          <button
            data-testid={HERO.ctaPrimary}
            onClick={scrollToGenerator}
            className="group flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-300 hover:-translate-y-1"
          >
            <Sparkles size={15} className="group-hover:rotate-12 transition-transform" />
            Create Your Child's Free Story
          </button>
          <button
            data-testid={HERO.ctaSecondary}
            onClick={() => document.getElementById("manifesto")?.scrollIntoView({ behavior: "smooth" })}
            className="flex items-center gap-2 px-6 py-3.5 rounded-full border border-purple-500/30 text-slate-300 font-medium text-sm hover:border-purple-400/60 hover:text-white transition-all duration-200"
          >
            <BookOpen size={14} />
            How it works
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={fadeUp(1.2)}
          initial="hidden"
          animate="visible"
          className="flex flex-wrap gap-6 lg:gap-10"
        >
          {STATS.map((s, i) => (
            <div key={i} className="flex flex-col">
              <span className="font-serif font-bold text-2xl gradient-text-gold">{s.value}</span>
              <span className="text-xs text-slate-500 mt-0.5">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-slate-500"
      >
        <span className="text-xs tracking-widest uppercase font-mono">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}>
          <ChevronDown size={16} />
        </motion.div>
      </motion.div>

      {/* Sample story excerpt cards — floating in background */}
      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-24 right-4 lg:right-12 hidden lg:block max-w-xs"
      >
        <div className="glass-card rounded-2xl p-4 shadow-xl shadow-purple-900/30">
          <div className="flex items-center gap-2 mb-2">
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <Star size={12} className="text-amber-400 fill-amber-400" />
          </div>
          <p className="text-sm text-slate-300 italic leading-relaxed font-serif">
            "Luna stepped into the moonlit garden, where fireflies danced around her name written in golden light..."
          </p>
          <p className="text-xs text-slate-500 mt-2">— Luna's Garden of Dreams</p>
        </div>
      </motion.div>
    </section>
  );
}
